import React, { Fragment, useContext, useState } from 'react';

import { deliveryInstructionApis } from '@apis/deliveryInstructionApis';
import { dashboardDeliveryRequestApis } from '@apis/next/deliveryRequestApis';
import { dashboardDeliveryRequestLogApis } from '@apis/next/deliveryRequestLogApis';
import { DeliveryRequestFormDialog } from '@components/admin/booking/DeliveryRequestFormDialog';
import { FileLink } from '@components/FileLink';
import { DeliveryInstructionContext } from '@components/utilities/Bookings/helpers';
import { deliveryRequestStrings } from '@data/deliveryRequest';
import { DeliveryRequestHelper } from '@models/next/DeliveryRequestHelper';
import { makeMuiStyles, useCommonStyles } from '@styles/index';
import { url } from '@utils/pages';
import { mapOptional, mapQueriesData, toJST } from '@utils/types';
import clsx from 'clsx';

const useStyles = makeMuiStyles((theme, common) => ({
  deliveryInstructionFilesContainer: {
    ...common.flexColumn,
  },
  deliveryInstructionFilesTitle: {
    ...common.flexInline,
    ...common.justifyBetween,
    ...common.itemsCenter,
    marginBottom: '0.5rem',
  },
  deliveryInstructionFileLinksContainer: {
    ...common.flexColumn,
    rowGap: '0.25rem',
  },
}));

const DeliveryRequestLastUpdate: React.FC<{ id: number }> = ({ id }) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const deliveryRequestQuery = dashboardDeliveryRequestApis.useGet(
    '/dashboard/delivery-request/:id',
    {
      params: { id },
    },
  );

  const deliveryRequestLogQuery = dashboardDeliveryRequestLogApis.useGet(
    '/dashboard/delivery-request-log/:id',
    {
      params: { id },
    },
  );

  return mapQueriesData(
    { deliveryRequest: deliveryRequestQuery, logs: deliveryRequestLogQuery },
    ({ deliveryRequest, logs }) => {
      return (
        <div className={clsx(classes.textGray500, classes.smallerText)} style={{ fontSize: 11 }}>
          {mapOptional(
            DeliveryRequestHelper.getLastUpdateUserAndTime(deliveryRequest, logs),
            (d) => `${d.action} (${d.time ? toJST(new Date(d.time)) : '-'})`,
          )}
        </div>
      );
    },
  );
};

export const DeliveryInstructionFiles = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const [deliveryRequestOpen, setDeliveryRequestOpen] = useState(false);

  const { d2dNo, deliveryInstruction, setDeliveryInstruction } = useContext(
    DeliveryInstructionContext,
  );

  return (
    <div className={classes.deliveryInstructionFilesContainer}>
      <DeliveryRequestFormDialog
        type="create"
        open={deliveryRequestOpen}
        onClose={async () => {
          setDeliveryRequestOpen(false);
          const updated = await deliveryInstructionApis.get(
            '/dashboard/delivery-instruction/{d2d_no}/',
            { path: { d2d_no: d2dNo } },
          );

          if (updated[200]) {
            setDeliveryInstruction(updated[200]);
          }
        }}
        d2dNo={d2dNo}
        deliveryInstruction={deliveryInstruction}
      />

      <div className={classes.deliveryInstructionFilesTitle}>
        <span className={clsx(classes.textBlack, classes.normalText, classes.fontWeight600)}>
          {deliveryRequestStrings.deliveryRequestDocuments}
        </span>
      </div>

      <div className={classes.deliveryInstructionFileLinksContainer}>
        {deliveryInstruction.delivery_requests.map((deliveryRequest) => (
          <Fragment key={deliveryRequest.id}>
            <FileLink
              variant="gray-block"
              href={
                url('/admin/booking/DeliveryRequestDetails') +
                `?d2d_no=${d2dNo}&id=${deliveryRequest.id}`
              }
              download={false}
            >
              {deliveryRequestStrings.deliveryRequestFile}
            </FileLink>
            <DeliveryRequestLastUpdate id={deliveryRequest.id} />
          </Fragment>
        ))}
      </div>

      {deliveryInstruction.delivery_requests.length === 0 && (
        <div className={classes.textBlack}>-</div>
      )}
    </div>
  );
};
